import type { FC } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'

import Filters from './components/Filters'
import FilterSelect from './components/FilterSelect'
import HelpGrid from './components/HelpGrid'

import data from './data'

import NeedSomethingElse from './components/NeedSomethingElse'
import useFilters from './data/useFilters'

import useFilteredData from './data/useFilteredData'

import type { ContentType, Role, Topic } from './data'

import ogImagePng from './assets/OgHelp.png'

type FilterOptions<TKey extends string> = { value: TKey | `all`; text: string }[]

const contentTypeOptions: FilterOptions<ContentType> = [
  { value: `all`, text: `All` },
  { value: `article`, text: `Articles` },
  { value: `course`, text: `Courses` },
  { value: `guide`, text: `Guides` },
  { value: `video`, text: `Videos` },
  { value: `webinar`, text: `Webinars` },
]
const topicOptions: FilterOptions<Topic> = [
  { value: `all`, text: `All` },
  { value: `analytics`, text: `Analytics` },
  { value: `identity-resolution`, text: `Identity resolution` },
  { value: `onboarding-resources`, text: `Onboarding resources` },
  { value: `tracking-best-practices`, text: `Tracking best practices` },
]
const roleOptions: FilterOptions<Role> = [
  { value: `all`, text: `All` },
  { value: `analytics`, text: `Analytics` },
  { value: `engineering`, text: `Engineering` },
  { value: `marketing`, text: `Marketing` },
  { value: `product`, text: `Product` },
]

const filterSelects = [
  { id: `contentType`, placeholder: `Type of content`, options: contentTypeOptions },
  { id: `topic`, placeholder: `Topic`, options: topicOptions },
  { id: `role`, placeholder: `Role`, options: roleOptions },
] as const

const Help: FC = () => {
  const theme = useTheme()
  const [query, setQuery] = useState(``)
  const [filters, setFilters] = useFilters()
  const filteredData = useFilteredData(data, filters)

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle="Help | Twilio Segment"
        pageDescription="Twilio Segment is trusted by thousands of companies as their Customer Data Platform. Collect user data with one API and send it to hundreds of tools or a data warehouse."
        pageOG={ogImagePng}
        themeColor={theme.palette.neutrals.white[100]}
      >
        <Navigation />
        <Hero query={query} onQueryChange={setQuery} />
        <Main>
          <Filters>
            {filterSelects.map(({ id, placeholder, options }) => (
              <FilterSelect
                key={id}
                id={id}
                placeholder={placeholder}
                options={options}
                onSelect={([selectedOption]) =>
                  setFilters({ [id]: selectedOption.value === `all` ? null : selectedOption.value })
                }
              />
            ))}
          </Filters>
          <HelpGrid data={filteredData} />
          <NeedSomethingElse />
        </Main>
      </Layout>
      <Footer />
    </AppearanceProvider>
  )
}

const Main = styled.main`
  /* Give a main content a background color, so sections don't flash from white background */
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

export default Help
